import * as React from "react"
import enUS from "date-fns/locale/en-US" // fallback

import { IntlRaw, Intl, intlDefault } from "../records/Intl"
import { Options, Values, translate } from "./translate"

export type Context = Intl & {
  onDebug: () => void
}

// Flow is just stupid and can't infer that intlDefault actually contains getLocale and translate properties
const context: React.Context<Context> = React.createContext({
  ...intlDefault,
  onDebug: () => {},
})

context.displayName = "IntlNitroContext"

const { Provider, Consumer: IntlNitroConsumer } = context

type Props = {
  children: React.ReactNode
  intl: IntlRaw
  locale?: Promise<typeof enUS>
}

const IntlNitroProvider = ({ children, intl, locale }: Props) => {
  const [debug, setDebug] = React.useState(false)

  const handleDebug = React.useCallback(() => {
    setDebug((state) => !state)
  }, [])

  const handleTranslate = React.useCallback(
    (key: string, values?: Values, opts?: Options) =>
      debug ? key : translate(intl.translations, key, values, opts),
    [debug, intl.translations],
  )

  const localePromise = React.useMemo(() => locale ?? Promise.resolve(enUS), [locale])

  return (
    <Provider
      value={{
        language: intl.language,
        translations: intl.translations,
        translate: handleTranslate,
        getLocale: localePromise,
        onDebug: handleDebug,
      }}
    >
      {children}
    </Provider>
  )
}

const useIntlNitro = () => React.useContext(context)

export { IntlNitroProvider, IntlNitroConsumer, useIntlNitro }

export default context
